import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useIntegration } from '~/hooks/useIntegration';
import { HubSpotFormConfig } from '~/components/HubSpotFormConfig';
import { AttioFormConfig } from '~/components/AttioFormConfig';
import { SlackFormConfig } from '~/components/SlackFormConfig';
import { Badge } from '~/components/ui/badge';
import type { IFormSchema } from '~/types';

interface IFormIntegrationsProps {
  formId: string;
  formSchema: IFormSchema;
}

export const FormIntegrations = ({ formId, formSchema }: IFormIntegrationsProps) => {
  const hubspot = useIntegration('hubspot');
  const attio = useIntegration('attio');
  const slack = useIntegration('slack');
  const [expanded, setExpanded] = useState(false);

  const isLoading = hubspot.loading || attio.loading || slack.loading;

  const hubSpotIntegration = hubspot.connected ? hubspot.integration : null;
  const attioIntegration = attio.connected ? attio.integration : null;
  const slackIntegration = slack.connected ? slack.integration : null;

  const connectedNames: string[] = [];
  if (hubSpotIntegration) connectedNames.push('HubSpot');
  if (attioIntegration) connectedNames.push('Attio');
  if (slackIntegration) connectedNames.push('Slack');

  const hasIntegrations = connectedNames.length > 0;

  /* Nothing connected and done loading — hide entirely */
  if (!isLoading && !hasIntegrations) return null;

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <button
        type="button"
        onClick={() => !isLoading && hasIntegrations && setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-4 text-left"
        disabled={isLoading || !hasIntegrations}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold">Integrations</h3>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!isLoading && hasIntegrations && (
            <div className="flex items-center gap-1.5">
              {connectedNames.map((name) => (
                <Badge key={name} variant="secondary" className="text-xs py-0 px-1.5">
                  {name}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {!isLoading && hasIntegrations && (
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {expanded && !isLoading && (
        <div className="px-4 pb-4 space-y-4">
          {hubSpotIntegration && (
            <HubSpotFormConfig formId={formId} integrationId={hubSpotIntegration.id} formSchema={formSchema} />
          )}
          {attioIntegration && (
            <AttioFormConfig formId={formId} integrationId={attioIntegration.id} formSchema={formSchema} />
          )}
          {slackIntegration && <SlackFormConfig formId={formId} integrationId={slackIntegration.id} />}
        </div>
      )}
    </div>
  );
};
